import { CardBrand, Money } from '@afterpay/types'
import { ColorTypes } from '@afterpaytouch/core'
import { PaymentStatus, OrderPaymentSchedule, OrderPaymentResponses, OrderRefundResponse, Donation, PaymentSource } from '@afterpaytouch/portal-api'
import { TFunction } from 'next-i18next'
import { isValidMoney } from '../Money/utils'
import { OrderScheduleIcons } from './constants'
import { isFullyRefunded } from '../../state/orders/utils'

interface GetPaymentLabelArgs {
  installment: OrderPaymentSchedule
  isDownPayment: boolean
  numberOfInstallments: number
}

interface GetLineItemsArgs {
  installment: OrderPaymentSchedule
  donation: Donation
  isFirstInstallment: boolean
}

export enum OrderScheduleLineItemTypes {
  Payment = 'Payment',
  Refund = 'Refund',
  Auxiliary = 'Auxiliary',
}

export interface OrderScheduleLineItemAuxiliary {
  createdDate: number
  amount: Money
  paymentMethodType: string
  displayName: string
  cardBrand?: CardBrand
}

interface OrderScheduleLineItem {
  type: OrderScheduleLineItemTypes
  item: OrderPaymentResponses | OrderRefundResponse | OrderScheduleLineItemAuxiliary
}

export const isAmountOwing = (installment: OrderPaymentSchedule): boolean => {
  return parseFloat(installment.owedAmount.amount) > 0
}
export const isInstallmentWaived = (installment: OrderPaymentSchedule): boolean => {
  const { status } = installment ?? {}
  const isWaived =
    installment.paymentResponses.length > 0 && installment.paymentResponses.every((payment) => payment.paymentSource === PaymentSource.WAIVED_BY_ADMIN)

  return status === PaymentStatus.PAID && isWaived
}

// @ts-ignore: OPERATION BLEED STOPPER
export const getPaymentStatus = (installment: OrderPaymentSchedule): string => {
  const { status } = installment ?? {}
  const isRefunded = isFullyRefunded(installment)
  const isWaived = isInstallmentWaived(installment)

  if (isRefunded) {
    return 'orders:paymentLabels:refunded'
  }

  if (isWaived) {
    return 'Waived'
  }

  switch (status) {
    case PaymentStatus.PAID:
      return 'orders:paymentLabels:paid'
    case PaymentStatus.PAYMENT_PENDING:
      return 'orders:paymentLabels:pending'
    case PaymentStatus.OWED:
      return 'orders:paymentLabels:owing'
    case PaymentStatus.REFUNDED:
      return 'orders:paymentLabels:cancelled'
    case PaymentStatus.PARTIAL_REFUND:
      return 'orders:paymentLabels:partialRefund'
    case PaymentStatus.OVERDUE:
      return 'orders:paymentLabels:overdue'
  }
}

export const getPaymentLabel = ({ installment, isDownPayment, numberOfInstallments }: GetPaymentLabelArgs, t: TFunction): string => {
  const { installmentSequence } = installment
  const offsetDownpayment = (sequence: number): number => (isDownPayment ? sequence - 1 : sequence)

  const getLabel = (): string => {
    if (isDownPayment && installmentSequence === 1) {
      return 'orders:orderSchedule:downPayment'
    }
    // If the last installment, it's the final payment
    if (installmentSequence === numberOfInstallments) {
      return 'orders:orderSchedule:finalPayment'
    }

    return 'orders:orderSchedule:paymentSequence'
  }
  return t('orders:orderSchedule:paymentLabel', {
    label: t(getLabel(), {
      installmentSequence: offsetDownpayment(installmentSequence),
      numberOfInstallments: offsetDownpayment(numberOfInstallments),
    }),
    paymentStatus: t(getPaymentStatus(installment)),
  })
}

export const getBorderClass = (isCashTheme: boolean, isSchedulePaid: boolean, isSchedulePayable: boolean): string => {
  if (isCashTheme) {
    return isSchedulePaid ? 'border-dark' : 'border-dark-subtle'
  }
  return isSchedulePayable ? 'border-dark-subtle' : 'border-primary'
}

export const getTextColor = (
  isScheduleOverdue: boolean,
  isSchedulePaid: boolean,
  isNextDue: boolean,
  isPartialRefund: boolean,
  isCashTheme: boolean
): ColorTypes => {
  if (isScheduleOverdue) {
    return 'Fire'
  }
  const isIncomplete = isSchedulePaid || isNextDue || isPartialRefund
  if (isIncomplete || isCashTheme) {
    return 'Black'
  }

  return 'Gray50'
}

export const getScheduleIcon = (installment: OrderPaymentSchedule, isNextDue: boolean): OrderScheduleIcons => {
  const { paymentStatus } = installment ?? {}
  const isRefunded = isFullyRefunded(installment)
  const isWaived = isInstallmentWaived(installment)

  if (isRefunded) {
    return OrderScheduleIcons.Refunded
  }

  if (isWaived) {
    return OrderScheduleIcons.Cancelled
  }

  switch (paymentStatus) {
    case PaymentStatus.PAID:
      return OrderScheduleIcons.Completed
    case PaymentStatus.OWED:
      if (isNextDue) {
        return OrderScheduleIcons.NextDue
      }
      return OrderScheduleIcons.Owed
    case PaymentStatus.REFUNDED:
      return OrderScheduleIcons.Cancelled
    case PaymentStatus.OVERDUE:
      return OrderScheduleIcons.Overdue
    default:
      return OrderScheduleIcons.Completed
  }
}

export const isRefundDisplayNameReturned = (instalment: OrderPaymentResponses | OrderRefundResponse | OrderScheduleLineItemAuxiliary): boolean => {
  return instalment?.displayName !== undefined && instalment?.displayName !== null && instalment?.displayName !== ''
}

export const isPayment = (payment: OrderPaymentResponses | OrderRefundResponse | OrderScheduleLineItemAuxiliary): payment is OrderPaymentResponses => {
  return (payment as OrderPaymentResponses).status !== undefined
}

export const getLineItems = ({ installment, donation, isFirstInstallment }: GetLineItemsArgs): OrderScheduleLineItem[] => {
  const items = [
    ...installment.paymentResponses.flatMap((item) => [
      { type: OrderScheduleLineItemTypes.Payment, item },
      ...(isValidMoney(item?.includingFees)
        ? [
            {
              type: OrderScheduleLineItemTypes.Auxiliary,
              item: {
                createdDate: item.createdDate,
                amount: item.includingFees,
                paymentMethodType: item.paymentMethodType,
                displayName: 'orders:orderSchedule:lateFees',
              },
            },
          ]
        : []),
    ]),
    ...installment.paymentRefundResponses.flatMap((item) => [{ type: OrderScheduleLineItemTypes.Refund, item }]),
  ]

  if (isValidMoney(donation?.amount) && isFirstInstallment) {
    const [item] = installment?.paymentResponses ?? []
    items.push({
      type: OrderScheduleLineItemTypes.Auxiliary,
      item: {
        createdDate: item?.createdDate,
        amount: donation?.amount,
        paymentMethodType: item?.paymentMethodType,
        displayName: 'orders:orderSchedule:donation',
      },
    })
  }

  return items
}

export const hasLineItems = (items: OrderScheduleLineItem[]): boolean => items.length > 0

/**
 * Creates money object and overwrite the amount
 */
export const totalAmountFn = (amount: string, paymentObj: Money): Money => {
  return {
    ...paymentObj,
    amount,
  }
}
